import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Search, Loader2, CheckCircle, Clock, XCircle, Mail } from 'lucide-react';

const STATUS_MAP = {
  Approved: {
    icon: <CheckCircle size={40} className="text-green-500" />,
    color: 'bg-green-50 border-green-200',
    label: 'Approved ✓',
    labelColor: 'text-green-700 bg-green-100',
    message: 'Your registration has been approved! Check your email for Hall Ticket and Password.',
  },
  Pending: {
    icon: <Clock size={40} className="text-amber-500" />,
    color: 'bg-amber-50 border-amber-200',
    label: 'Pending Review',
    labelColor: 'text-amber-700 bg-amber-100',
    message: 'Your registration is under review by the admin. You will receive an email once approved.',
  },
  Rejected: {
    icon: <XCircle size={40} className="text-red-500" />,
    color: 'bg-red-50 border-red-200',
    label: 'Rejected',
    labelColor: 'text-red-700 bg-red-100',
    message: 'Your registration was not approved. Please contact the administration for more details.',
  },
};

export default function RegistrationStatusPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [checked, setChecked] = useState(false);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Please enter your email');
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/auth/student/status?email=${encodeURIComponent(email.toLowerCase())}`);
      setResult(data.data);
      setChecked(true);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('No registration found for this email.');
        setChecked(true);
        setResult(null);
      } else {
        toast.error(err.response?.data?.message || 'Failed to fetch status');
      }
    } finally {
      setLoading(false);
    }
  };

  const status = result ? STATUS_MAP[result.registrationStatus] || STATUS_MAP.Pending : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-10 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-sm mb-4 border border-gray-200">
          <img src="/bits-logo.png" alt="Logo" className="w-10 h-10 object-contain" />
        </div>
        <h1 className="text-3xl font-extrabold text-black">Registration Status</h1>
        <p className="mt-2 text-sm text-gray-500">Enter your registered email to check your application status</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-5 shadow-sm sm:rounded-xl sm:px-10 border border-gray-200 space-y-6">

          {/* Search form */}
          <form onSubmit={handleCheck} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Registered Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email" value={email} required
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-black focus:border-black sm:text-sm"
                />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium text-white bg-black hover:bg-gray-800 transition-colors">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
              {loading ? 'Checking...' : 'Check Status'}
            </button>
          </form>

          {/* Result */}
          {checked && !result && !loading && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center">
              <p className="text-gray-500 text-sm">No registration found for <strong>{email}</strong>.</p>
              <Link to="/student/register" className="text-sm text-black font-medium hover:underline mt-2 inline-block">
                Register now →
              </Link>
            </div>
          )}

          {result && status && (
            <div className={`border rounded-xl p-5 text-center space-y-3 ${status.color}`}>
              <div className="flex justify-center">{status.icon}</div>
              <div>
                <p className="text-lg font-bold text-gray-900">{result.name}</p>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full mt-1 inline-block ${status.labelColor}`}>
                  {status.label}
                </span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{status.message}</p>

              {/* Extra info */}
              <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-gray-200">
                <p>Email: <strong>{result.email}</strong></p>
                {result.hallTicketNumber && (
                  <p>Hall Ticket: <strong className="font-mono text-gray-900">{result.hallTicketNumber}</strong></p>
                )}
                <p>Registered on: <strong>{new Date(result.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></p>
              </div>
            </div>
          )}

          <div className="text-center pt-2 space-y-2">
            <Link to="/login" className="block text-sm text-gray-500 hover:text-black transition-colors">
              ← Back to Exam Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
